import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
const leadingLinks = [{
  name: 'About',
  href: '/#about'
}, {
  name: 'Services',
  href: '/#services'
}];
const trailingLinks = [{
  name: 'Contact',
  href: '/#contact'
}];
const projectsLinks = [{
  name: 'Residential',
  to: '/projects/residential'
}, {
  name: 'Commercial',
  to: '/projects/commercial'
}, {
  name: 'Recreational',
  to: '/projects/recreational'
}];
const homesLinks = [{
  name: 'Campanilla',
  href: 'https://campanilla.jrpdi.com.ph/'
}];
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isPastHero, setIsPastHero] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHomesOpen, setIsHomesOpen] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const isProjectsRoute = location.pathname.startsWith('/projects');
  // "solid" = dark text + glass background.
  // On the home page the hero is full-viewport, so keep the nav white/transparent
  // over the hero (where dark text would be illegible) and only flip to solid once
  // the user has scrolled past it. On interior pages the nav is always solid.
  const solid = !isHome || isPastHero;
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      // Flip a bit before the hero fully leaves so the transition feels natural.
      setIsPastHero(window.scrollY > window.innerHeight - 100);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
  const linkColor = solid ? 'text-text-muted' : 'text-white/80';
  return <motion.nav initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'py-4' : 'py-6'} ${solid ? 'bg-white/40 backdrop-blur-xl' : 'bg-transparent'}`} data-id="element-132">
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between" data-id="element-133">
        <a href="/" className="relative z-10 group flex items-center gap-3 md:gap-4" data-id="element-134">
          <img src="https://cdn.magicpatterns.com/uploads/fw2aQonvEkwKvdfzcJjTfo/2.png" alt="JRP Design Inc." className={`h-8 md:h-10 w-auto transition-all duration-500 ${solid ? '' : 'brightness-0 invert'}`} data-id="element-135" />
          <span className={`block h-6 w-px transition-colors duration-500 ${solid ? 'bg-neutral-300' : 'bg-white/40'}`} data-id="element-136" />
          <span className={`block text-[10px] md:text-xs uppercase tracking-widest font-light leading-tight transition-colors duration-500 ${linkColor}`} data-id="element-137">
            Architecture &<br data-id="element-138" />
            Design Studio
          </span>
        </a>

        <div className="hidden md:flex items-center space-x-8" data-id="element-139">
          {leadingLinks.map(link => <a key={link.name} href={link.href} className={`text-sm tracking-widest uppercase font-light transition-colors duration-300 hover:text-accent ${linkColor}`} data-id="element-140">
              {link.name}
            </a>)}

          {/* Projects dropdown */}
          <div className="relative" onMouseEnter={() => setIsProjectsOpen(true)} onMouseLeave={() => setIsProjectsOpen(false)} data-id="element-141">
            <button onClick={() => navigate('/projects/residential')} className={`flex items-center gap-1 text-sm tracking-widest uppercase font-light transition-colors duration-300 hover:text-accent ${isProjectsRoute ? 'text-accent' : linkColor}`} aria-haspopup="true" aria-expanded={isProjectsOpen} data-id="element-142">
              Projects
              <ChevronDown size={14} className={`transition-transform duration-300 ${isProjectsOpen ? 'rotate-180' : ''}`} data-id="element-143" />
            </button>

            <AnimatePresence data-id="element-144">
              {isProjectsOpen && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 8
            }} transition={{
              duration: 0.2,
              ease: [0.16, 1, 0.3, 1]
            }} className="absolute left-0 top-full pt-4 min-w-[180px]" data-id="element-145">
                  <div className="bg-white/90 backdrop-blur-xl border border-neutral-200/50 py-2" data-id="element-146">
                    {projectsLinks.map(link => {
                  const active = location.pathname === link.to;
                  return <Link key={link.name} to={link.to} className={`block px-5 py-2 text-sm tracking-widest uppercase font-light transition-colors duration-300 hover:text-accent ${active ? 'text-accent' : 'text-text-muted'}`} data-id="element-147">
                          {link.name}
                        </Link>;
                })}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {trailingLinks.map(link => <a key={link.name} href={link.href} className={`text-sm tracking-widest uppercase font-light transition-colors duration-300 hover:text-accent ${linkColor}`} data-id="element-148">
              {link.name}
            </a>)}

          {/* JRP Homes dropdown */}
          <div className="relative" onMouseEnter={() => setIsHomesOpen(true)} onMouseLeave={() => setIsHomesOpen(false)} data-id="element-149">
            <button className={`flex items-center gap-1 text-sm tracking-widest uppercase font-light transition-colors duration-300 hover:text-accent ${linkColor}`} aria-haspopup="true" aria-expanded={isHomesOpen} data-id="element-150">
              JRP Homes
              <ChevronDown size={14} className={`transition-transform duration-300 ${isHomesOpen ? 'rotate-180' : ''}`} data-id="element-151" />
            </button>

            <AnimatePresence data-id="element-152">
              {isHomesOpen && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 8
            }} transition={{
              duration: 0.2,
              ease: [0.16, 1, 0.3, 1]
            }} className="absolute right-0 top-full pt-4 min-w-[180px]" data-id="element-153">
                  <div className="bg-white/90 backdrop-blur-xl border border-neutral-200/50 py-2" data-id="element-154">
                    {homesLinks.map(link => <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer" className="block px-5 py-2 text-sm tracking-widest uppercase font-light text-text-muted hover:text-accent transition-colors duration-300" data-id="element-155">
                        {link.name}
                      </a>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        <button className={`md:hidden relative z-[60] p-2 transition-colors ${isMobileMenuOpen ? 'text-primary hover:text-accent' : solid ? 'text-primary hover:text-accent' : 'text-white hover:text-accent'}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-156">
          {isMobileMenuOpen ? <X size={24} data-id="element-157" /> : <Menu size={24} data-id="element-158" />}
        </button>

        <AnimatePresence data-id="element-159">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="absolute top-0 left-0 w-full h-screen bg-white flex flex-col items-center justify-center space-y-6 z-50" data-id="element-160">
              {leadingLinks.map(link => <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-3xl font-light text-primary hover:text-accent transition-colors tracking-widest uppercase leading-tight" data-id="element-161">
                  {link.name}
                </a>)}

              <div className="flex flex-col items-center space-y-3" data-id="element-162">
                <span className="text-3xl font-light text-primary tracking-widest uppercase leading-tight" data-id="element-163">
                  Projects
                </span>
                {projectsLinks.map(link => <Link key={link.name} to={link.to} onClick={() => setIsMobileMenuOpen(false)} className={`text-lg font-light transition-colors tracking-widest uppercase leading-tight ${location.pathname === link.to ? 'text-accent' : 'text-text-muted hover:text-accent'}`} data-id="element-164">
                    {link.name}
                  </Link>)}
              </div>

              {trailingLinks.map(link => <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-3xl font-light text-primary hover:text-accent transition-colors tracking-widest uppercase leading-tight" data-id="element-165">
                  {link.name}
                </a>)}

              <div className="flex flex-col items-center space-y-3" data-id="element-166">
                <span className="text-3xl font-light text-primary tracking-widest uppercase leading-tight" data-id="element-167">
                  JRP Homes
                </span>
                {homesLinks.map(link => <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-light text-text-muted hover:text-accent transition-colors tracking-widest uppercase leading-tight" data-id="element-168">
                    {link.name}
                  </a>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.nav>;
}